/**
 * Event types for the TruMetriX service.
 * @enum {string}
 */
export const EventType = {
	/** A view event is an event that doesn't require an interaction. i.e. user watched 50% of a video. */
	VIEW: "View",
	/** An interaction event is any event that requires user input */
	INTERACTION: "Interaction",
};

/**
 * Action types for the TruMetriX service.
 * @enum {string}
 */
export const ActionType = {
	/** An open action is what you would use most of the time. Any time an action is used it will by default be Open. */
	OPEN: "Open",
	/** A close action is when you want to report a close. i.e. closed a panel, closed an image. */
	CLOSE: "Close",
};

/**
 * Events for the TruMetriX service.
 * @enum {string}
 */
export const Event = {
	/** An impression event is where a user has ben confirmed to have seen something.*/
	IMPRESSION: "Impression",
	/** A click event happens when the user clicks a button. */
	CLICK: "Click",
	/** A watch event will be used for start, 50% video watched, 10 seconds watched, etc.*/
	WATCH: "Watch",
	/** An engage event is any touch event that is not specifically a button click. i.e. model spin*/
	ENGAGE: "Engage",
	/** A submit event is whenvever a user submits information into a form.*/
	SUBMIT: "Submit",
	/** An on landing event marking a first time visitor.*/
	NEW_VISIT: "NewVisit",
	/** An on landing event marking a return visitor.*/
	RETURN_VISIT: "ReturnVisit",
};

/**
 * Environments for the TruMetriX service. These are cascading down
 * @enum {string}
 */
export const Environment = {
	APP: "app",
	COMPONENT: "component",
	SCENE: "scene",
	BUTTON: "button",
	ENGAGEMENT: "engagement",
	MEDIA: "media",
};

/**
 * Tags for the TruMetriX service.
 * @property {string} product - The product name.
 * @property {string} productType - The product type.
 * @property {string} moduleType - The module type.
 * @property {string[]} freeform - An array of freeform tags.
 */
export function EnvTags() {
	return {
		product: null, // Consider providing a default value or ensure your logic handles `null`.
		productType: null,
		moduleType: null,
		freeform: [],
	};
}

import axios from "axios";
/**
 * Represents a TruMetriX service for recording metrics.
 * @class
 * @property {string} filePath - File path to the metrics configuration file.
 */
export class TruMetriXService {
	constructor(filepath) {
		this.electron = false;
		if (
			typeof process !== "undefined" &&
			process.versions &&
			process.versions.electron
		) {
			this.electron = true;this.electron = true;
			this.fs = window.require("fs");
			this.path = window.require("path");
			this.fileWriterQueue = Promise.resolve();
		}

		this.curEnvironment = {
			client: null,
			show: null,
			zone: null,
			app: null,
			component: null,
			scene: null,
			button: null,
			media: null,
			engagement: null,
			appTags: null,
			componentTags: null,
			sceneTags: null,
			buttonTags: null,
			mediaTags: null,
			engagementTags: null,
		};

		let metricsFile = filepath || "./metrics.config.json";
		if (this.electron) {
			try {
				const configFileContent = this.fs.readFileSync(metricsFile, "utf8");
				const config = JSON.parse(configFileContent);
				this.setConfig(config);
			} catch (error) {
				if (error.code === "ENOENT") {
					console.error(`File not found: ${metricsFile}`);
				} else {
					console.error(`An error occurred: ${error.message}`);
				}
			}
		} else {
			fetch(metricsFile)
				.then((response) => response.json())
				.then(this.setConfig)
				.catch(() =>
					console.error(
						"Make sure to have a metrics.config.json file in the root of your project if you want to use the TruMetriX service."
					)
				);
		}
	}

	/**
	 * Add the write operation to the queue
	 * @param {string} filePath 
	 * @param {object} parsedData 
	 * @returns 
	 */
	async addToWriteQueue(filePath, parsedData) {
        // Add the write operation to the queue
        this.fileWriterQueue = this.fileWriterQueue.then(() => this._writeToFile(filePath, parsedData));
        return this.fileWriterQueue;
    }

	/**
	 * Write the data to the file
	 * @param {string} filePath 
	 * @param {object} parsedData 
	 * @returns 
	 */
	async _writeToFile(filePath, parsedData) {
        return new Promise((resolve, reject) => {
            this.fs.open(filePath, 'r+', (err, fd) => {
                if (err && err.code === 'ENOENT') {
                    // File does not exist, so create it and write the initial data
                    let initialData = {};
                    initialData[this.generateUTCTimestamp()] = parsedData;
                    this.fs.writeFile(filePath, JSON.stringify(initialData, null, 4), (err) => {
                        if (err) return reject(err);
                        resolve();
                    });
                } else if (err) {
                    return reject(err);
                } else {
                    // File exists, append new data
                    this.fs.fstat(fd, (err, stat) => {
                        if (err) return reject(err);
                        // Go to one character before the end to overwrite the last }
                        let position = stat.size - 1;
                        // Prepare the string to be appended
                        let appendStr = `,${'\n'}"${this.generateUTCTimestamp()}": ${JSON.stringify(parsedData, null, 4)}\n}`;

                        // Read the existing file content
                        this.fs.readFile(filePath, 'utf-8', (err, fileContent) => {
                            if (err) return reject(err);
                            // Remove the last character (assumed to be '}')
                            fileContent = fileContent.slice(0, -1);
                            // Append the new data
                            fileContent += appendStr;

                            // Write the updated content back to the file
                            this.fs.writeFile(filePath, fileContent, 'utf-8', (err) => {
                                if (err) return reject(err);
                                this.fs.close(fd, (err) => {
                                    if (err) return reject(err);
                                    resolve();
                                });
                            });
                        });
                    });
                }
            });
        });
    }

	/**
	 * Set the configuration for the metrics service.
	 * @param {object} json - The configuration object.
	 */
	setConfig = (json) => {
		this.apiKey = json.apiKey;
		this.metricsURLs = json.urls;

		this.curEnvironment.client = json?.environment?.client ?? null;
		this.curEnvironment.show = json?.environment?.show ?? null;
		this.curEnvironment.zone = json?.environment?.zone ?? null;

		// Check for null values and log warnings
		if (!this.curEnvironment.client) console.warn("Warning: client is null");
		if (!this.curEnvironment.show) console.warn("Warning: show is null");
		if (!this.curEnvironment.zone) console.warn("Warning: zone is null");

		this.curEnvironment.app = json.environment.app;
		this.app = json.environment.app;

		this.logging = json.logging;
		this.localport = json.localport;

		if (this.electron) {
			let self = this;
			const { ipcRenderer } = require("electron");
			async function callCheckMAL() {
				try {
					const result = await ipcRenderer.invoke("checkMAL");
					self.curEnvironment.show = result?.environment?.show ?? null;
					self.curEnvironment.zone = result?.environment?.zone ?? null;
				} catch (error) {
					console.error("Error invoking checkMAL:", error);
				}
			}
			callCheckMAL();
		} else if (this.localport) {
			fetch("http://localhost:" + this.localport + "/checkMAL", {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
				},
			})
				.then((response) => response.json())
				.then((dataResponse) => {
					const config = dataResponse;
					this.curEnvironment.show = config?.environment?.show ?? null;
					this.curEnvironment.zone = config?.environment?.zone ?? null;
				})
				.catch((error) => {
					console.error("Error:", error);
				});

			// Additional check for app since it's directly assigned without null coalescing
		}

		if (!this.curEnvironment.app) console.warn("Warning: app is null");
	};

	/**
	 * Set the configuration for the metrics service.
	 * @param {object} json - The configuration object.
	 */
	updateConfig = (json) => {
		if (json.apiKey) this.apiKey = json.apiKey;
		if (json.urls) this.metricsURLs = json.urls;

		if (json?.environment?.client)
			this.curEnvironment.client = json.environment.client;
		if (json?.environment?.show)
			this.curEnvironment.show = json.environment.show;
		if (json?.environment?.zone)
			this.curEnvironment.zone = json.environment.zone;

		if (json.environment.app) this.curEnvironment.app = json.environment.app;
		if (json.environment.app) this.app = json.environment.app;

		if (json.logging) this.logging = json.logging;
		if (json.localport) this.localport = json.localport;

		// Check for null values and log warnings
		if (!this.curEnvironment.client) console.warn("Warning: client is null");
		if (!this.curEnvironment.show) console.warn("Warning: show is null");
		if (!this.curEnvironment.zone) console.warn("Warning: zone is null");

		// Additional check for app since it's directly assigned without null coalescing
		if (!this.curEnvironment.app) console.warn("Warning: app is null");
	};

	/**
	 * Send a request to the server.
	 * @param {string} obj - The body of the request.
	 */
	sendToServers(obj) {
		let urls = this.metricsURLs;

		if (!urls) {
			return console.error(
				"No URLs provided for metrics service, or config hasn't been loaded."
			);
		}

		Promise.all(
			urls.map((url) =>
				axios
					.post(url, obj, {
						headers: {
							"Content-Type": "application/json",
						},
					})
					.then((response) => response.data)
					.catch((error) => console.error("Error fetching:", url, error))
			)
		).then((responses) => {
			// All Axios requests have completed
			this.logging && console.log("All Axios requests have completed");
		});
	}

	/**
	 * Record a metric.
	 * @param {Event} event - The event type.
	 */
	recordInteractionEvent = (event = null) => {
		if (event === null) {
			return console.error("Missing event type for metrics call!");
		}

		const obj = JSON.stringify({
			key: this.apiKey,
			pn: this.app,
			e: event,
			et: EventType.INTERACTION,
			at: ActionType.OPEN,
			env: this.curEnvironment,
			sid: this.sessionID,
		});

		this.sendToServers(obj);
		this.writeToFile(obj);
		this.logging && console.log(obj);
	};

	/**
	 * Record a close Event
	 * @param {Environment} environment - The environment to close. App, Component, Hotspot, Scene, or Video.
	 */
	recordCloseEvent = (environment = null) => {
		const obj = JSON.stringify({
			key: this.apiKey,
			pn: this.app,
			e: Event.CLICK,
			et: EventType.INTERACTION,
			at: ActionType.CLOSE,
			env: this.curEnvironment,
			sid: this.sessionID,
		});

		this.sendToServers(obj);
		this.writeToFile(obj);

		// If no environment is specified, try to determine the environment from the current environment.
		if (environment === null) {
			if (this.curEnvironment.media !== null) {
				environment = Environment.MEDIA;
			} else if (this.curEnvironment.button !== null) {
				environment = Environment.BUTTON;
			} else if (this.curEnvironment.engagement !== null) {
				environment = Environment.ENGAGEMENT;
			} else if (this.curEnvironment.scene !== null) {
				environment = Environment.SCENE;
			} else if (this.curEnvironment.component !== null) {
				environment = Environment.COMPONENT;
			}
		}

		// Clear the environment if it is specified.
		if (environment != null) {
			switch (environment) {
				case Environment.APP:
					this.setApp(null);
					break;
				case Environment.COMPONENT:
					this.setComponent(null);
					break;
				case Environment.SCENE:
					this.setScene(null);
					break;
				case Environment.BUTTON:
					this.setButton(null);
					break;
				case Environment.MEDIA:
					this.setMedia(null);
					break;
				case Environment.ENGAGEMENT:
					this.setInteractive(null);
					break;
			}
		}

		this.logging && console.log(obj);
	};

	/**
	 * Record a media Event
	 * @param {number} value - The value(s) to record this should be a percentage or a time.
	 */
	recordWatchEvent = (value) => {
		if (this.curEnvironment.media === null) {
			console.error(
				"Missing video name for metrics call! Make sure to set the video name in the environment before calling recordWatchEvent"
			);
			return;
		}

		const obj = JSON.stringify({
			key: this.apiKey,
			pn: this.app,
			e: Event.WATCH,
			et: EventType.VIEW,
			at: ActionType.OPEN,
			env: this.curEnvironment,
			sid: this.sessionID,
		});

		this.sendToServers(obj);
		this.writeToFile(obj);
		this.logging && console.log(obj);
	};

	generateUTCTimestamp = () => {
		return Math.floor(new Date().getTime()).toString();
	};
	
	writeToFile(data) {
		if (this.electron) {
			let parsedData = JSON.parse(data);
			// Path to the JSON file
			const filePath = this.path.join("./", "output.json");
			
			
			this.addToWriteQueue(filePath, parsedData);
		} else if (this.localport) {
			fetch("http://localhost:" + this.localport + "/sendMetrics", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ string: data }),
			})
				.then((response) => response.text())
				.then((dataResponse) => {
					console.log(dataResponse);
				})
				.catch((error) => {
					console.error("Error:", error);
				});
		}
	}

	/**
	 * Set the current app name for metrics. This will clear out other environment variables
	 * @param {string} app
	 * @param {EnvTags} tags - The Tags to assign to the object.
	 */
	setApp = (app, tags = null) => {
		this.curEnvironment.app = app;
		this.curEnvironment.appTags = null;

		this.curEnvironment.component = null;
		this.curEnvironment.componentTags = null;

		this.curEnvironment.scene = null;
		this.curEnvironment.sceneTags = null;

		this.curEnvironment.media = null;
		this.curEnvironment.mediaTags = null;

		this.curEnvironment.button = null;
		this.curEnvironment.buttonTags = null;

		this.curEnvironment.engagement = null;
		this.curEnvironment.engagementTags = null;

		if (tags) {
			this.curEnvironment.appTags = tags;
		}

		if (app !== null) {
			this.recordInteractionEvent(Event.CLICK);
		}
	};

	/**
	 * Set the current component name for metrics. This will clear out other environment variables, other than the app.
	 * @param {string} component - The component name.
	 * @param {EnvTags} tags - The Tags to assign to the object.
	 */
	setComponent = (component, tags = null) => {
		this.curEnvironment.component = component;
		this.curEnvironment.componentTags = null;

		this.curEnvironment.scene = null;
		this.curEnvironment.sceneTags = null;

		this.curEnvironment.media = null;
		this.curEnvironment.mediaTags = null;

		this.curEnvironment.button = null;
		this.curEnvironment.buttonTags = null;

		this.curEnvironment.engagement = null;
		this.curEnvironment.engagementTags = null;

		if (tags) {
			this.curEnvironment.componentTags = tags;
		}

		if (component !== null) {
			this.recordInteractionEvent(Event.CLICK);
		}
	};

	/**
	 * Set the current scene name for metrics. This will clear out button, media, and engagement.
	 * @param {string} scene - The scene name.
	 * @param {EnvTags} tags - The Tags to assign to the object.
	 */
	setScene = (scene, tags = null) => {
		this.curEnvironment.scene = scene;
		this.curEnvironment.sceneTags = null;

		this.curEnvironment.media = null;
		this.curEnvironment.mediaTags = null;

		this.curEnvironment.button = null;
		this.curEnvironment.buttonTags = null;

		this.curEnvironment.engagement = null;
		this.curEnvironment.engagementTags = null;

		if (tags) {
			this.curEnvironment.sceneTags = tags;
		}

		if (scene !== null) {
			this.recordInteractionEvent(Event.CLICK);
		}
	};

	/**
	 * Set the current button name for metrics. This will clear out the media and engagement.
	 * @param {string} button - The button name.
	 * @param {EnvTags} tags - The Tags to assign to the object.
	 * @param {bool} noClear - option to not clear out the other environments
	 */
	setButton = (button, tags = null, noClear = false) => {
		if (!noClear) {
			this.curEnvironment.media = null;
			this.curEnvironment.mediaTags = null;

			this.curEnvironment.engagement = null;
			this.curEnvironment.engagementTags = null;
		}

		this.curEnvironment.button = button;
		this.curEnvironment.buttonTags = null;

		if (tags) {
			this.curEnvironment.buttonTags = tags;
		}

		if (button !== null) {
			this.recordInteractionEvent(Event.CLICK);
		}
	};

	/**
	 * Set the current media name for metrics. This will clear out the button and engagement.
	 * @param {string} media - The media name.
	 * @param {EnvTags} tags - The Tags to assign to the object.
	 * @param {bool} noClear - option to not clear out the other environments
	 */
	setMedia = (media, tags = null, noClear = false) => {
		if (!noClear) {
			this.curEnvironment.button = null;
			this.curEnvironment.buttonTags = null;

			this.curEnvironment.engagement = null;
			this.curEnvironment.engagementTags = null;
		}

		this.curEnvironment.media = media;
		this.curEnvironment.mediaTags = null;

		if (tags) {
			this.curEnvironment.mediaTags = tags;
		}

		if (media !== null) {
			this.recordWatchEvent(0);
		}
	};

	/**
	 * Set the current engagement name for metrics. this will also clear out the button and media.
	 * @param {string} engagement - The media name.
	 * @param {EnvTags} tags - The Tags to assign to the object.
	 * @param {bool} noClear - option to not clear out the other environments
	 */
	setEngagement = (engagement, tags = null, noClear = false) => {
		if (!noClear) {
			this.curEnvironment.media = null;
			this.curEnvironment.mediaTags = null;

			this.curEnvironment.button = null;
			this.curEnvironment.buttonTags = null;
		}

		this.curEnvironment.engagement = engagement;
		this.curEnvironment.engagementTags = null;

		if (tags) {
			this.curEnvironment.engagementTags = tags;
		}

		if (engagement !== null) {
			this.recordInteractionEvent(Event.CLICK);
		}
	};

	/**
	 * Set the session ID for the metrics service.
	 * @param {string} sessionID
	 */
	setSessionID = (sessionID) => {
		this.sessionID = sessionID;
	};
}

export const truMetriXService = new TruMetriXService();
export const truEventType = EventType;
export const truEvent = Event;
export const truActionType = ActionType;
export const truEnvironment = Environment;
export const truEnvTags = EnvTags;
